import { Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';

declare var require: any;

export interface Point {
  x: number;
  y: number;
  time: number;
}

export type PointGroup = Array<Point>;

@Component({
  selector: 'svc-signature-pad',
  templateUrl: './signature-pad.component.html',
  styleUrls: ['./signature-pad.component.css'],
})
export class SignaturePadComponent {
  @Input() public options: Object;
  @Input() public savedSignature: string; // currently only used in new customer signature dialog 29/11/2023
  @Output() public onBeginEvent: EventEmitter<boolean>;
  @Output() public onEndEvent: EventEmitter<boolean>;

  private signaturePad: any;
  private canvas: any;
  private sp: any = require('signature_pad')['default'];
  private elementRef: ElementRef;

  constructor(elementRef: ElementRef) {
    // no op
    this.elementRef = elementRef;
    this.options = this.options || {};
    this.onBeginEvent = new EventEmitter();
    this.onEndEvent = new EventEmitter();
  }

  public ngAfterContentInit(): void {
    this.canvas = this.elementRef.nativeElement.querySelector('canvas');
    this.signaturePad = new this.sp(this.canvas, this.options);

    if (this.savedSignature) {
      this.drawImageScaled(this.savedSignature);
    }

    if ((<any>this.options)['canvasHeight']) {
      this.canvas.height = (<any>this.options)['canvasHeight'];
    }

    if ((<any>this.options)['canvasWidth']) {
      this.canvas.width = (<any>this.options)['canvasWidth'];
    }
    this.signaturePad.onBegin = this.onBegin.bind(this);
    this.signaturePad.onEnd = this.onEnd.bind(this);
  }

  public resizeCanvas(canvasWidth?: number): void {
    if (canvasWidth) {
      this.canvas.width = canvasWidth;
    } else {
      // When zoomed out to less than 100%, for some very strange reason,
      // some browsers report devicePixelRatio as less than 1
      // and only part of the canvas is cleared then.
      // const ratio: number = Math.max(window.devicePixelRatio || 1, 1);
      // const canvas: any = this.signaturePad.canvas;
      // canvas.width = canvas.offsetWidth * ratio;
      // canvas.height = canvas.offsetHeight * ratio;
      // canvas.getContext('2d').scale(ratio, ratio);
      // this.signaturePad.clear(); // otherwise isEmpty() might return incorrect value
    }
  }

  // Returns signature image as an array of point groups
  public toData(): Array<PointGroup> {
    return this.signaturePad.toData();
  }

  // Draws signature image from an array of point groups
  public fromData(points: Array<PointGroup>): void {
    this.signaturePad.fromData(points);
  }

  // Returns signature image as data URL (see https://mdn.io/todataurl for the list of possible paramters)
  public toDataURL(imageType?: string, quality?: number): string {
    return this.signaturePad.toDataURL(imageType, quality); // save image as data URL
  }

  // Draws signature image from data URL
  public fromDataURL(dataURL: string, options: Object = {}): void {
    this.signaturePad.fromDataURL(dataURL, options);
  }

  // Draw, scale and center signature image from data URL,
  public drawImageScaled(dataUrl: string): void {
    const canvas: HTMLCanvasElement = document.getElementsByTagName('canvas')[0];
    const image = new Image();
    image.src = dataUrl;
    image.onload = function () {
      const canvasContext: CanvasRenderingContext2D = canvas.getContext('2d');
      const wrh = image.width / image.height;
      let newWidth = canvas.width;
      let newHeight = newWidth / wrh;
      if (newHeight > canvas.height) {
        newHeight = canvas.height;
        newWidth = newHeight * wrh;
      }
      const xOffset = newWidth < canvas.width ? (canvas.width - newWidth) / 2 : 0;
      const yOffset = newHeight < canvas.height ? (canvas.height - newHeight) / 2 : 0;

      canvasContext.drawImage(image, xOffset, yOffset, newWidth, newHeight);
    };
  }

  // Clears the canvas
  public clear(): void {
    this.signaturePad.clear();
  }

  // Returns true if canvas is empty, otherwise returns false
  public isEmpty(): boolean {
    return this.signaturePad.isEmpty();
  }

  // Unbinds all event handlers
  public off(): void {
    this.signaturePad.off();
  }

  // Rebinds all event handlers
  public on(): void {
    this.signaturePad.on();
  }

  // set an option on the signaturePad - e.g. set('minWidth', 50);
  public set(option: string, value: any): void {
    console.log(this.signaturePad);
    switch (option) {
      case 'canvasHeight':
        this.signaturePad.canvas.height = value;
        break;
      case 'canvasWidth':
        this.signaturePad.canvas.width = value;
        break;
      default:
        this.signaturePad[option] = value;
    }
  }

  // notify subscribers on signature begin
  public onBegin(): void {
    this.onBeginEvent.emit(true);
  }

  // notify subscribers on signature end
  public onEnd(): void {
    this.onEndEvent.emit(true);
  }

  public queryPad(): any {
    return this.signaturePad;
  }

  public removeBlanks(): any {
    const canvas: HTMLCanvasElement = document.getElementsByTagName('canvas')[0];
    const croppedCanvas: HTMLCanvasElement = document.createElement('canvas');
    const croppedCtx: CanvasRenderingContext2D = croppedCanvas.getContext('2d');

    croppedCanvas.width = canvas.width;
    croppedCanvas.height = canvas.height;
    croppedCtx.drawImage(canvas, 0, 0);

    let w = croppedCanvas.width;
    let h = croppedCanvas.height;
    const pix: any = { x: [], y: [] };
    const imageData = croppedCtx.getImageData(0, 0, w, h);
    let index = 0;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        index = (y * w + x) * 4;
        if (imageData.data[index + 3] > 0) {
          pix.x.push(x);
          pix.y.push(y);
        }
      }
    }

    pix.x.sort((a: any, b: any) => a - b);
    pix.y.sort((a: any, b: any) => a - b);
    const n = pix.x.length - 1;

    w = pix.x[n] - pix.x[0];
    h = pix.y[n] - pix.y[0];

    if (pix.x[0] >= 0 && pix.y[0] >= 0 && w >= 0 && h >= 0) {
      const cut = croppedCtx.getImageData(pix.x[0], pix.y[0], w, h);

      croppedCanvas.width = w;
      croppedCanvas.height = h;
      croppedCtx.putImageData(cut, 0, 0);

      return croppedCanvas.toDataURL();
    } else {
      return null;
    }
  }
}
